import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import { PlanFeatureValue } from '../data/plan-features.data'
import { PlatformEvent, PlatformEventList } from '../models/platform-event.model'
import { generateFlaggedThemes, MockFlaggedTheme } from '../mocks/mock-theme-moderation'

/** Simulated network latency for the one remaining mock endpoint (flagged themes — see below). */
const MOCK_DELAY = 250

interface PaginatedResponse<T> {
  success: boolean
  data: {
    results: T[]
    totalResults: number
    currentPage: number
    limit: number
    lastPage: number
  }
}

interface ApiResponse<T> {
  success: boolean
  data: T
}

@Injectable({ providedIn: 'root' })
export class AdminApiService {
  private http = inject(HttpClient)
  private baseUrl = `${environment.apiUrl}/api/v1/admin`

  // ─── Platform ─────────────────────────────────────────────
  getMe(): Observable<ApiResponse<{ sub: string; email: string; name: string | null; groups: string[]; role: string; permissions: string[] }>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/me`)
  }

  getOverview(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/overview`)
  }

  getActionLogs(params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/action-logs`, {
      params: this.buildParams(params),
    })
  }

  confirmUser(userId: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/users/${userId}/confirm`, {})
  }

  // ─── Stores — reads ───────────────────────────────────────
  getStores(params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/stores`, {
      params: this.buildParams(params),
    })
  }

  getStore(storeId: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/stores/${storeId}`)
  }

  getBillingHistory(storeId: string): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/stores/${storeId}/billing-history`)
  }

  getAutomationUsage(storeId: string, params: Record<string, any> = {}): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/stores/${storeId}/automation-usage`, {
      params: this.buildParams(params),
    })
  }

  getCreditLog(storeId: string, params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/stores/${storeId}/credits/log`, {
      params: this.buildParams(params),
    })
  }

  getStorePromotions(storeId: string): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/stores/${storeId}/promotions`)
  }

  getStoreAuditLogs(storeId: string, params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/stores/${storeId}/audit-logs`, {
      params: this.buildParams(params),
    })
  }

  // ─── Stores — lifecycle commands ──────────────────────────
  extendTrial(storeId: string, body: { days?: number; until?: string; reason?: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/stores/${storeId}/trial/extend`, body)
  }

  extendSubscription(storeId: string, body: { days?: number; until?: string; reason?: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/stores/${storeId}/subscription/extend`, body)
  }

  activateStore(storeId: string, body: { plan?: string; months?: number; reason: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/stores/${storeId}/activate`, body)
  }

  suspendStore(storeId: string, body: { reason: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/stores/${storeId}/suspend`, body)
  }

  reinstateStore(storeId: string, body: { reason?: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/stores/${storeId}/reinstate`, body)
  }

  grantCredits(storeId: string, body: { amount: number; reason: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/stores/${storeId}/credits/grant`, body)
  }

  setTestFlag(storeId: string, body: { isTest: boolean; reason?: string }): Observable<ApiResponse<{ isTest: boolean }>> {
    return this.http.post<ApiResponse<{ isTest: boolean }>>(`${this.baseUrl}/stores/${storeId}/test-flag`, body)
  }

  // ─── Impersonation ────────────────────────────────────────
  impersonateStore(
    storeId: string,
    body: { reason: string },
  ): Observable<
    ApiResponse<{ code: string; expiresIn: number; session: { jti: string; storeName: string; ownerEmail: string; expiresAt: string } }>
  > {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/stores/${storeId}/impersonate`, body)
  }

  getImpersonationSessions(): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/impersonation-sessions`)
  }

  revokeImpersonation(jti: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/impersonation-sessions/${jti}/revoke`, {})
  }

  // ─── Store verification requests ("توثيق المتجر الإلكتروني") ─
  //   GET   /verification-requests            paginated list, filter by ?status=
  //   GET   /verification-requests/:id        full request incl. document URLs
  //   POST  /verification-requests/:id/approve  { reason?: string }
  //   POST  /verification-requests/:id/reject   { reason: string }
  getVerificationRequests(params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/verification-requests`, {
      params: this.buildParams(params),
    })
  }

  getVerificationRequest(requestId: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/verification-requests/${requestId}`)
  }

  approveVerificationRequest(requestId: string, body: { reason?: string } = {}): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/verification-requests/${requestId}/approve`, body)
  }

  rejectVerificationRequest(requestId: string, body: { reason: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/verification-requests/${requestId}/reject`, body)
  }

  // ─── Help Center (categories + FAQ items) ────────────────
  getHelpCenterCategories(): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/help-center/categories`)
  }

  createHelpCenterCategory(body: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/help-center/categories`, body)
  }

  updateHelpCenterCategory(categoryId: string, body: any): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.baseUrl}/help-center/categories/${categoryId}`, body)
  }

  deleteHelpCenterCategory(categoryId: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.baseUrl}/help-center/categories/${categoryId}`)
  }

  getHelpCenterItems(categoryId: string): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/help-center/categories/${categoryId}/items`)
  }

  createHelpCenterItem(categoryId: string, body: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/help-center/categories/${categoryId}/items`, body)
  }

  updateHelpCenterItem(categoryId: string, itemId: string, body: any): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.baseUrl}/help-center/categories/${categoryId}/items/${itemId}`, body)
  }

  deleteHelpCenterItem(categoryId: string, itemId: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.baseUrl}/help-center/categories/${categoryId}/items/${itemId}`)
  }

  // ─── Help Center — full articles (categories/tags/media/quick filter) ──
  getHelpArticles(params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/help-center/articles`, {
      params: this.buildParams(params),
    })
  }

  getHelpArticleTags(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.baseUrl}/help-center/articles/tags`)
  }

  getHelpArticle(articleId: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/help-center/articles/${articleId}`)
  }

  createHelpArticle(body: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/help-center/articles`, body)
  }

  updateHelpArticle(articleId: string, body: any): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.baseUrl}/help-center/articles/${articleId}`, body)
  }

  deleteHelpArticle(articleId: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.baseUrl}/help-center/articles/${articleId}`)
  }

  /** Uploads one image for an article's `image` block — returns `{url, fileKey}`. */
  uploadHelpArticleMedia(file: File): Observable<ApiResponse<{ url: string; fileKey: string }>> {
    const formData = new FormData()
    formData.append('file', file)
    return this.http.post<ApiResponse<{ url: string; fileKey: string }>>(`${this.baseUrl}/help-center/media`, formData)
  }

  // ─── Events (public /events pages on the landing site) ────
  /** Paginated list incl. drafts — params: `page`, `limit`, `q`, `status` ('draft' | 'published'). */
  getEvents(params: Record<string, string | number | undefined> = {}): Observable<ApiResponse<PlatformEventList>> {
    return this.http.get<ApiResponse<PlatformEventList>>(`${this.baseUrl}/events`, { params: this.buildParams(params) })
  }

  getEvent(eventId: string): Observable<ApiResponse<PlatformEvent>> {
    return this.http.get<ApiResponse<PlatformEvent>>(`${this.baseUrl}/events/${eventId}`)
  }

  createEvent(body: Record<string, unknown>): Observable<ApiResponse<PlatformEvent>> {
    return this.http.post<ApiResponse<PlatformEvent>>(`${this.baseUrl}/events`, body)
  }

  updateEvent(eventId: string, body: Record<string, unknown>): Observable<ApiResponse<PlatformEvent>> {
    return this.http.patch<ApiResponse<PlatformEvent>>(`${this.baseUrl}/events/${eventId}`, body)
  }

  deleteEvent(eventId: string): Observable<ApiResponse<unknown>> {
    return this.http.delete<ApiResponse<unknown>>(`${this.baseUrl}/events/${eventId}`)
  }

  publishEvent(eventId: string): Observable<ApiResponse<PlatformEvent>> {
    return this.http.post<ApiResponse<PlatformEvent>>(`${this.baseUrl}/events/${eventId}/publish`, {})
  }

  unpublishEvent(eventId: string): Observable<ApiResponse<PlatformEvent>> {
    return this.http.post<ApiResponse<PlatformEvent>>(`${this.baseUrl}/events/${eventId}/unpublish`, {})
  }

  /** Event thumbnail/cover images reuse the Help Center media upload — returns `{url, fileKey}`. */
  uploadEventMedia(file: File): Observable<ApiResponse<{ url: string; fileKey: string }>> {
    return this.uploadHelpArticleMedia(file)
  }

  // ─── Discount codes ───────────────────────────────────────
  getDiscountCodes(params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/discount-codes`, {
      params: this.buildParams(params),
    })
  }

  createDiscountCode(body: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/discount-codes`, body)
  }

  updateDiscountCode(discountId: string, body: any): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.baseUrl}/discount-codes/${discountId}`, body)
  }

  // ─── Promotions ───────────────────────────────────────────
  getPromotions(): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/promotions`)
  }

  updatePromotion(promotionId: string, body: any): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.baseUrl}/promotions/${promotionId}`, body)
  }

  // ─── Store content drill-down (control gap #1) ─────────────
  getStoreOrders(storeId: string, params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/stores/${storeId}/orders`, {
      params: this.buildParams(params),
    })
  }

  getStoreProducts(storeId: string, params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/stores/${storeId}/products`, {
      params: this.buildParams(params),
    })
  }

  getStoreCustomers(storeId: string, params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/stores/${storeId}/customers`, {
      params: this.buildParams(params),
    })
  }

  // ─── Support tickets (control gap #2), Zammad-backed, cross-store ──
  getTickets(params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http
      .get<{ success: boolean; data: any[]; pagination: { total: number; lastPage: number; currentPage: number; limit: number } }>(
        `${this.baseUrl}/tickets`,
        { params: this.buildParams(params) },
      )
      .pipe(
        map(res => ({
          success: res.success,
          data: {
            results: res.data,
            totalResults: res.pagination.total,
            currentPage: res.pagination.currentPage,
            limit: res.pagination.limit,
            lastPage: res.pagination.lastPage,
          },
        })),
      )
  }

  getTicket(ticketId: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/tickets/${ticketId}`)
  }

  updateTicket(ticketId: string, body: { status?: string; priority?: string }): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.baseUrl}/tickets/${ticketId}`, body)
  }

  addTicketReply(ticketId: string, body: { message: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/tickets/${ticketId}/messages`, body)
  }

  // ─── Per-feature override (control gap #3) ─────────────────
  getStoreFeatureOverrides(storeId: string): Observable<ApiResponse<Record<string, PlanFeatureValue>>> {
    return this.http
      .get<ApiResponse<{ overrides: Record<string, PlanFeatureValue> }>>(`${this.baseUrl}/stores/${storeId}/feature-overrides`)
      .pipe(map(res => ({ success: res.success, data: res.data.overrides || {} })))
  }

  setStoreFeatureOverride(storeId: string, featureKey: string, value: PlanFeatureValue): Observable<ApiResponse<Record<string, PlanFeatureValue>>> {
    return this.http
      .post<ApiResponse<{ overrides: Record<string, PlanFeatureValue> }>>(`${this.baseUrl}/stores/${storeId}/feature-overrides`, {
        featureKey,
        value,
      })
      .pipe(map(res => ({ success: res.success, data: res.data.overrides || {} })))
  }

  resetStoreFeatureOverride(storeId: string, featureKey: string): Observable<ApiResponse<Record<string, PlanFeatureValue>>> {
    return this.http
      .delete<ApiResponse<{ overrides: Record<string, PlanFeatureValue> }>>(`${this.baseUrl}/stores/${storeId}/feature-overrides/${featureKey}`)
      .pipe(map(res => ({ success: res.success, data: res.data.overrides || {} })))
  }

  // ─── Billing refund + failing charges (control gap #4) ─────
  // NOT a live gateway reversal — see admin/application/commands/billingRefund.ts
  // on the backend: no vendor-billing gateway (Kashier included) supports a
  // programmatic refund in this codebase yet. `paymentId` is the billing-
  // history row's own `_id` (not `merchantOrderId`), and `amount` is required —
  // both came from `bill._id`/`bill.amount` on the billing-history row the
  // store-details page already has.
  refundBillingCharge(paymentId: string, body: { amount: number; reason: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/billing/${paymentId}/refund`, body)
  }

  getFailingCharges(params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/billing/failing-charges`, {
      params: this.buildParams(params),
    })
  }

  /** Re-arm a failing renewal so the billing worker charges the card on its next pass. */
  retryFailingCharge(storeId: string, body: { reason?: string } = {}): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/billing/failing-charges/${storeId}/retry`, body)
  }

  /** Mark a failing renewal settled out of band (clears attempts + failure; reason required). */
  resolveFailingCharge(storeId: string, body: { reason: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/billing/failing-charges/${storeId}/resolve`, body)
  }

  getRenewalQueue(params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/billing/renewal-queue`, {
      params: this.buildParams(params),
    })
  }

  // ─── Content moderation queue (control gap #6) ─────────────
  getModerationQueue(params: Record<string, any> = {}): Observable<PaginatedResponse<any>> {
    return this.http.get<PaginatedResponse<any>>(`${this.baseUrl}/moderation/queue`, {
      params: this.buildParams(params),
    })
  }

  moderationDismiss(itemId: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/moderation/${itemId}/dismiss`, {})
  }

  moderationTakedown(itemId: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/moderation/${itemId}/takedown`, {})
  }

  // ─── Affiliate program rollup (control gap #7) ─────────────
  // Platform-wide counts only (links/clicks/revenue-by-currency) — per-
  // creator `conversions`/`payoutOwed`/fraud `flagged` are NOT computed
  // here: real commission money is per-store/per-rate/per-currency (see
  // marketing/affiliate's `getAffiliateReport`), and summing that across
  // every store's currency would produce a number that means nothing.
  getAffiliatesRollup(): Observable<ApiResponse<{ affiliates: any[]; summary: any }>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/affiliates/rollup`).pipe(
      map(res => ({
        success: res.success,
        data: {
          affiliates: (res.data.topLinks || []).map((l: any) => ({
            name: l.creatorName,
            storeName: l.storeName,
            clicks: l.clicks,
            conversions: null,
            payoutOwed: null,
            flagged: false,
          })),
          summary: {
            totalLinks: res.data.totalLinks,
            activeLinks: res.data.activeLinks,
            totalClicks: res.data.totalClicks,
            storesUsingAffiliates: res.data.storesUsingAffiliates,
            revenueByCurrency: res.data.revenueByCurrency,
          },
        },
      })),
    )
  }

  // ─── Webhook health (control gap #8, surfaced on Store Details) ─
  getStoreWebhookHealth(storeId: string, _storeName?: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/stores/${storeId}/webhook-health`)
  }

  // ─── Credit anomaly detection (control gap #9) ─────────────
  getCreditAnomalies(): Observable<ApiResponse<any[]>> {
    return this.http
      .get<PaginatedResponse<any>>(`${this.baseUrl}/credits/anomalies`, { params: this.buildParams({ limit: 100 }) })
      .pipe(map(res => ({ success: res.success, data: res.data.results })))
  }

  markCreditAnomalyReviewed(anomalyId: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/credits/anomalies/${anomalyId}/review`, {})
  }

  markCreditAnomalyDismissed(anomalyId: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/credits/anomalies/${anomalyId}/dismiss`, {})
  }

  // ─── Announcements (control gap #11) ────────────────────────
  // Delivered to merchants via take-theme's own AnnouncementsService, which
  // reads the filtered GET /v1/announcements (not this admin-authenticated one).
  getAnnouncements(): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/announcements`)
  }

  getAnnouncement(announcementId: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/announcements/${announcementId}`)
  }

  createAnnouncement(body: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/announcements`, body)
  }

  updateAnnouncement(announcementId: string, body: any): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.baseUrl}/announcements/${announcementId}`, body)
  }

  toggleAnnouncement(announcementId: string, isActive: boolean): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/announcements/${announcementId}/toggle`, { isActive })
  }

  deleteAnnouncement(announcementId: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.baseUrl}/announcements/${announcementId}`)
  }

  uploadAnnouncementMedia(file: File): Observable<ApiResponse<{ url: string; fileKey: string }>> {
    const formData = new FormData()
    formData.append('file', file)
    return this.http.post<ApiResponse<{ url: string; fileKey: string }>>(`${this.baseUrl}/announcements/media`, formData)
  }

  // ─── GDPR data requests (control gap #12) ───────────────────
  // "Hard delete" in the UI still only calls markProcessed — it never
  // triggers a real export/delete job (there isn't one; see the backend's
  // dataRequest model doc-comment). Record-keeping only.
  getDataRequests(): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/data-requests`)
  }

  logDataRequest(body: { storeId: string; type: 'export' | 'delete'; subjectEmail?: string; notes?: string }): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/data-requests`, body)
  }

  markDataRequestProcessed(requestId: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/data-requests/${requestId}/process`, {})
  }

  // ─── Business/dashboard stats (stats gaps #1-3, #6, #8-10) ──
  getBusinessStats(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/stats/business`)
  }

  /** COD block policy adoption: stores with the policy on and at least one enabled rule. */
  getCodPolicyAdoption(): Observable<ApiResponse<{ storesWithRules: number; enabledLast30Days: number }>> {
    return this.http.get<ApiResponse<{ storesWithRules: number; enabledLast30Days: number }>>(`${this.baseUrl}/stats/cod-policy`)
  }

  // ─── Theme moderation (control gap #13, P2) — STILL MOCK ────
  // No theme-marketplace submission model exists anywhere in the backend
  // (`premadeTheme` is a staff-curated catalog, not merchant-submitted
  // content) — there is nothing real to query. Left as the one deliberate
  // mock exception rather than wiring a real endpoint with nothing behind it.
  private flaggedThemes: MockFlaggedTheme[] = generateFlaggedThemes()

  getFlaggedThemes(): Observable<ApiResponse<any[]>> {
    return new Observable(subscriber => {
      const t = setTimeout(() => {
        subscriber.next({ success: true, data: this.flaggedThemes })
        subscriber.complete()
      }, MOCK_DELAY)
      return () => clearTimeout(t)
    })
  }

  flaggedThemeDismiss(itemId: string): Observable<ApiResponse<any>> {
    const item = this.flaggedThemes.find(t => t._id === itemId)
    if (item) item.status = 'dismissed'
    return new Observable(subscriber => {
      const t = setTimeout(() => {
        subscriber.next({ success: !!item, data: item })
        subscriber.complete()
      }, MOCK_DELAY)
      return () => clearTimeout(t)
    })
  }

  flaggedThemeTakedown(itemId: string): Observable<ApiResponse<any>> {
    const item = this.flaggedThemes.find(t => t._id === itemId)
    if (item) item.status = 'taken_down'
    return new Observable(subscriber => {
      const t = setTimeout(() => {
        subscriber.next({ success: !!item, data: item })
        subscriber.complete()
      }, MOCK_DELAY)
      return () => clearTimeout(t)
    })
  }

  // ─── API key visibility (control gap #14, P2) ───────────────
  // `usage` (hourly request series) has no real source — API keys only
  // track `lastUsedAt`/`usageCount` in aggregate, not a timestamped log —
  // so it's an empty series (an honestly-empty chart) rather than fabricated.
  getStoreApiKeys(storeId: string): Observable<ApiResponse<{ keys: any[]; usage: any[] }>> {
    return this.http
      .get<ApiResponse<any[]>>(`${this.baseUrl}/stores/${storeId}/api-keys`)
      .pipe(map(res => ({ success: res.success, data: { keys: res.data, usage: [] } })))
  }

  // ─── Domain/DNS health (control gap #15, P2) ─────────────────
  // No SSL-certificate check exists outside the k8s cert-manager
  // provisioning flow (worker-only infra) — `sslStatus` is a proxy off the
  // stored `verified` flag, not a live certificate inspection.
  // Every custom domain the store has, not just the first one.
  getStoreDomainHealth(
    storeId: string,
    _domain?: string,
  ): Observable<ApiResponse<{ domain: string; sslStatus: string; dnsStatus: string; createdAt: string | null }[]>> {
    return this.http.get<ApiResponse<{ domains: any[] }>>(`${this.baseUrl}/stores/${storeId}/domain-health`).pipe(
      map(res => ({
        success: res.success,
        data: (res.data?.domains || []).map(d => ({
          domain: d.name,
          sslStatus: d.verified ? 'valid' : 'unverified',
          dnsStatus: d.resolvable ? 'resolved' : 'not_resolved',
          createdAt: d.createdAt ?? null,
        })),
      })),
    )
  }

  // ─── Store health score (stats gap #13, P2) ───────────────────
  getStoreHealthScore(storeId: string): Observable<ApiResponse<{ score: number }>> {
    return this.http.get<ApiResponse<{ score: number }>>(`${this.baseUrl}/stores/${storeId}/health-score`)
  }

  // ─── Geographic breakdown (stats gap #14, P2) ─────────────────
  getGeoBreakdown(): Observable<ApiResponse<any[]>> {
    return this.http
      .get<ApiResponse<{ byCountry: any[] }>>(`${this.baseUrl}/stats/geo-breakdown`)
      .pipe(map(res => ({ success: res.success, data: res.data.byCountry })))
  }

  // ─── Helpers ──────────────────────────────────────────────
  private buildParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams()
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined || value === '') continue
      // Multi-select filters go over the wire comma-separated (`?status=live,readonly`).
      if (Array.isArray(value)) {
        const list = value.filter(v => v !== null && v !== undefined && v !== '')
        if (list.length) httpParams = httpParams.set(key, list.join(','))
        continue
      }
      httpParams = httpParams.set(key, String(value))
    }
    return httpParams
  }
}
